import React from 'react';

import { Text } from '@change/ds-core-ui/next';

import { translationKey } from '@change-corgi/core/i18n';
import type { TranslationKey } from '@change-corgi/core/i18n';
import { useI18n } from '@change-corgi/core/react/i18n';

import { useSharingTools } from '../../../../hooks/useSharingTools';
import { PLATFORM_CONFIG } from '../../../../shared/constants';
import { SharingToolsCardWrapperVariant1 } from '../../../SharingToolsCardWrapper';
import type { GroupData } from '../RecommendedGroupCardsContainer/types';
import { RecommendedGroupsCardLoading } from '../RecommendedGroupsCardLoading';

/**
 * Displays a preview card for recommended groups with platform-specific styling.
 * Shows a loading state when no group data is provided.
 *
 * ```tsx
 * <RecommendedGroupsCardPreviewVariant1
 *   group={{
 *     name: 'r/California',
 *     followers: 11456,
 *     iconUrl: 'https://...',
 *     platform: 'reddit'
 *   }}
 * />
 * ```
 */
export const RecommendedGroupsCardPreviewVariant1: React.FC<Props> = ({
	group,
	titleKey = translationKey('corgi.petition.starter_dashboard_v2.sharing_tools.recommended_groups.title_variant1'),
	descriptionKey = translationKey(
		'corgi.petition.starter_dashboard_v2.sharing_tools.recommended_groups.description_variant1',
	),
}) => {
	const { localizeNumber } = useI18n();

	const { actions } = useSharingTools();

	if (!group) {
		return <RecommendedGroupsCardLoading title={titleKey} description={descriptionKey} />;
	}

	const { name, followers, platform, iconUrl, bgColor: groupBgColor } = group;

	const { Icon, bgColor } = PLATFORM_CONFIG[platform];

	return (
		<SharingToolsCardWrapperVariant1
			title={titleKey}
			description={descriptionKey}
			primaryActionOnClick={actions.navigateToGroupRecommendations}
		>
			<article
				className="h-[168px] w-[144.12px] overflow-visible rounded-xl bg-white shadow-sm"
				aria-label={`${platform} group: ${name}`}
			>
				<figure
					className="bg-fill-weak relative flex h-24 items-center justify-center overflow-visible rounded-t-xl border-0 border-solid border-white bg-cover bg-center bg-no-repeat"
					style={{ backgroundColor: groupBgColor || bgColor }}
				>
					<div
						className="absolute -right-[4px] -top-[4px] z-10 flex h-[32px] w-[32px] items-center justify-center rounded-full border shadow-md"
						style={{ backgroundColor: bgColor }}
						role="img"
						aria-label={`${platform} platform`}
					>
						<Icon className="text-white" size={18} aria-hidden="true" />
					</div>

					{iconUrl ? (
						<div
							className="bg-fill-base aspect-square h-[67px] w-[67px] rounded-full bg-contain bg-center bg-no-repeat shadow"
							style={{
								backgroundImage: `url(${iconUrl})`,
								imageRendering: 'crisp-edges',
							}}
							role="img"
							aria-label={`${name} logo`}
						/>
					) : (
						<div className="h-14 w-14 rounded-full border-4 border-white shadow" aria-hidden="true">
							<div className="to-fill-error h-full w-full rounded-full bg-gradient-to-b from-transparent" />
						</div>
					)}
				</figure>

				<section className="bg flex w-36 flex-col items-start justify-center rounded-b-xl p-3">
					<Text as="h4" variant="heading" size="xx-small" className="text-typography-strong">
						{name}
					</Text>
					{followers !== null && (
						<Text as="p" variant="body" size="x-small" className="text-typography-weak">
							{localizeNumber(followers)} followers
						</Text>
					)}
				</section>
			</article>
		</SharingToolsCardWrapperVariant1>
	);
};

type Props = Readonly<{
	group?: GroupData;
	titleKey?: TranslationKey;
	descriptionKey?: TranslationKey;
}>;
